import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, RevealLines } from "@/components/motion";

const CHAPTERS = [
  {
    no: "01",
    title: "The Intent",
    body: "Most agencies start with a logo brief. We start with an interrogation: what do you actually stand for, and why should anyone outside your building care? That's the truth we dig for first — because a personality built on a real answer is the only kind people remember once the pitch deck is closed.",
  },
  {
    no: "02",
    title: "The Strategy",
    body: "Visibility without positioning is just noise with a bigger budget. We build category strategy on data, not instinct — so every campaign, asset, and decision compounds toward one outcome: you're not competing for attention; you're the obvious answer before the question is asked.",
  },
  {
    no: "03",
    title: "The Legacy",
    body: "A logo can be redesigned in a weekend. Trust can't be faked in one. We engineer brands that compound — where every touchpoint makes the next one more believable, so the credibility you build this year is still earning for you a decade from now.",
  },
];


export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const clip = useTransform(
    scrollYProgress,
    [0.12, 0.42],
    ["inset(0% 0% 100% 0%)", "inset(0% 0% 0% 0%)"]
  );

  return (
    <section id="about" ref={ref} className="relative py-28 md:py-40" data-testid="manifesto">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-8">
              — Our Manifesto
            </p>
            <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight">
              <RevealLines lines={["Beyond logos", "and colours."]} />
            </h2>
            <div className="mt-10">
              <div className="relative overflow-hidden bg-[#1A1713] flex items-center justify-center h-[360px] md:h-[420px]">
                <div className="absolute -inset-10 bg-[#C5A059]/10 blur-[100px] rounded-full" />
                <motion.img
                  style={{ y, clipPath: clip }}
                  src="/aurelume-mark.png"
                  alt="Aurelume logo mark"
                  className="relative w-52 h-52 md:w-60 md:h-60 object-contain"
                />
              </div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mt-8 mb-4">
                The Story Behind the AURELUME Logo
              </h3>
              <p className="text-base text-[#1A1713]/70 leading-relaxed">
                Inspired by the meaning of AURELUME—Golden Light, the logo
                represents clarity, purpose, and transformation. At its heart,
                the sun symbolizes illumination, positivity, and the powerful
                aura every brand deserves. The continuous loops reflect the
                ever-evolving journey of branding—constant growth, innovation,
                and forward momentum. Together, they embody our belief that great
                brands are built through strategic thinking, creative excellence,
                and continuous evolution.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7 space-y-16 pt-4">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} i={i} className="border-t border-[#E8E4D9] pt-8">
                <div className="flex items-baseline gap-6">
                  <span className="font-serif text-2xl text-[#C5A059]">{c.no}</span>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-4">
                      {c.title}
                    </h3>
                    <p className="text-base md:text-lg text-[#1A1713]/70 leading-relaxed">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
